import { useEffect, useRef } from 'react';

/**
 * Keeps an editor's state in step with a value that arrives after mount.
 *
 * A surface that was handed a list row seeds its editors from it, but the
 * unsent draft only reaches it once the by-id fetch resolves (the row carries
 * no `notesDraft` — see visitDetailsQuery). A plain `useState` initialiser runs
 * once, so the editor kept the empty string it was born with and a visit with a
 * draft opened a blank editor.
 *
 * Re-seeding only while the editor is untouched is what makes this safe: once
 * the professional has typed anything, a late-arriving source is recorded but
 * never written over their work.
 */
export function useSeededEditorValue(
  source: string,
  value: string,
  setValue: (next: string) => void,
): void {
  const seededRef = useRef(source);

  useEffect(() => {
    if (seededRef.current === source) return;

    const isUntouched = value === seededRef.current;
    seededRef.current = source;

    if (isUntouched) setValue(source);
    // `value` is read to tell an untouched editor from an edited one; the guard
    // above keeps this a no-op on every keystroke.
  }, [source, value, setValue]);
}
